import React, { useEffect, useState } from 'react';
import DefaultLayout from '@/layout/DefaultLayout';
import Breadcrumb from '@/components/Breadcrumb';
import { default as DefaultInput } from '@/components/Input';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next'; // Import the useTranslation hook
import {
  ProjectOwner,
  getProjectOwner,
  saveProjectOwner,
} from '@/services/ProjectOwnerService';
import Spinner from '@/components/Spinner';
import { useNavigate, useParams } from 'react-router-dom';
import { getCompanyId } from '@/services/AuthService';
import { toast } from 'react-hot-toast';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import FormButtons from '@/components/shared/FormButtons';

const validationSchema = Yup.object().shape({
  name: Yup.string().required('ProjectOwnerForm.nameRequired'),
});

interface FormValues extends ProjectOwner {
  name: string;
}

function ProjectOwnerForm() {
  const { id } = useParams();
  const editForm = id !== 'new';
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { t } = useTranslation(); // Initialize the useTranslation hook

  const [formikValuesState, setFormikValuesState] = useState<FormValues>({
    name: '',
    id: '',
  });
  const [loading, setLoading] = useState(false);

  const {
    data: projectOwnerData,
    isLoading,
    refetch: refetchProjectOwner,
  } = useQuery<ProjectOwner | undefined>(
    ['projectOwner', id],
    async () => await getProjectOwner(id!),
    {
      enabled: editForm,
      refetchOnWindowFocus: false,
      staleTime: 6000,
      keepPreviousData: true,
      onSuccess: (projectOwner) => {
        if (projectOwner) {
          setFormikValuesState((prev) => ({ ...prev, ...projectOwner }));
        }
      },
    }
  );

  useEffect(() => {
    if (editForm && projectOwnerData) {
      // Update form values with project owner data
      setFormikValuesState((prev) => ({ ...prev, ...projectOwnerData }));
    }
  }, [editForm, projectOwnerData]);

  const handleSaveProjectOwner = async (
    values: FormValues,
    { resetForm, validateForm }: any
  ) => {
    setLoading(true);

    try {
      const companyId = getCompanyId();
      const projectOwner: ProjectOwner = {
        ...values,
        companyId: companyId!,
      };

      const data = await saveProjectOwner(projectOwner);
      if (data) {
        if (editForm) {
          toast.success(
            t('SuccessUpdateMessage', {
              name: t('ToastMsg.ProjectOwner'),
            })
          );
        } else {
          toast.success(
            t('SuccessMessage', {
              name: t('ToastMsg.ProjectOwner'),
            })
          );
        }
      }
      if (data && editForm) {
        refetchProjectOwner();
      }
      queryClient.invalidateQueries(['projectOwners']);
      navigate(-1);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
      resetForm();
    }
  };

  return (
    <DefaultLayout>
      <Breadcrumb
        pageName={
          editForm
            ? t('ProjectOwnerForm.editProjectOwner')
            : t('ProjectOwnerForm.addProjectOwner')
        }
      />
      {loading ? (
        <Spinner />
      ) : (
        <Formik
          initialValues={formikValuesState}
          validationSchema={validationSchema}
          onSubmit={handleSaveProjectOwner}
          enableReinitialize={editForm}
        >
          {({ values, errors, handleChange, handleSubmit, resetForm }) => (
            <form onSubmit={handleSubmit}>
              <div className="group relative z-0 mb-6 w-full">
                <DefaultInput
                  type="text"
                  name="name"
                  id="name"
                  placeholder=" "
                  onChange={handleChange}
                  errors={errors}
                  value={values.name}
                  label={t('ProjectOwnerForm.nameLabel')}
                />
              </div>

              <FormButtons resetForm={resetForm} loading={loading} />
            </form>
          )}
        </Formik>
      )}
    </DefaultLayout>
  );
}

export default ProjectOwnerForm;
